import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { programsAPI, patientsAPI } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Layers, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABELS = { performance: "Rendimiento", clinical: "Clínico" };
const EMPTY = { patient_id: "", title: "", type: "performance", goal: "", start_date: "", weeks: 8 };

export default function ProgramsPage() {
  const navigate = useNavigate();
  const [cycles, setCycles] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const fetchCycles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await programsAPI.list();
      setCycles(res.data.program_cycles || []);
    } catch {
      toast.error("Error cargando programas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCycles(); }, [fetchCycles]);
  useEffect(() => {
    patientsAPI.list({ limit: 200 }).then((r) => setPeople(r.data.patients || [])).catch(() => {});
  }, []);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.patient_id) { toast.error("Selecciona un atleta o paciente"); return; }
    if (!form.title.trim()) { toast.error("El título es obligatorio"); return; }
    setSaving(true);
    try {
      const res = await programsAPI.create({ ...form, weeks: Number(form.weeks) || 0 });
      toast.success("Programa creado");
      setShowNew(false);
      setForm(EMPTY);
      navigate(`/programas/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="programs-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">Programas</h1>
          <p className="text-muted-foreground text-sm mt-0.5">Mesociclos y planes de entrenamiento</p>
        </div>
        <Button size="sm" className="rounded-full" onClick={() => { setForm(EMPTY); setShowNew(true); }} data-testid="new-program-btn">
          <Plus className="w-4 h-4 mr-1.5" />
          Nuevo programa
        </Button>
      </div>

      {loading ? (
        <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-16 rounded-xl bg-muted animate-pulse" />)}</div>
      ) : cycles.length > 0 ? (
        <div className="space-y-2">
          {cycles.map((c) => (
            <Card key={c.id} className="cursor-pointer hover:border-primary/30 transition-colors" onClick={() => navigate(`/programas/${c.id}`)} data-testid={`program-card-${c.id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                  <Layers className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{c.title}</p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className="text-xs text-muted-foreground">{c.patient_name || "—"}</span>
                    <Badge variant="secondary" className="text-[10px]">{TYPE_LABELS[c.type] || c.type}</Badge>
                    <Badge variant={c.status === "active" ? "default" : "secondary"} className="text-[10px]">
                      {c.status === "active" ? "Activo" : "Completado"}
                    </Badge>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-muted-foreground">
          <Layers className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-medium">Aún no hay programas</p>
          <Button variant="link" size="sm" className="mt-1" onClick={() => setShowNew(true)}>Crear el primero</Button>
        </div>
      )}

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="new-program-dialog">
          <DialogHeader><DialogTitle>Nuevo programa</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label>Atleta / Paciente *</Label>
              <Select value={form.patient_id} onValueChange={(v) => setField("patient_id", v)}>
                <SelectTrigger data-testid="program-patient-select"><SelectValue placeholder="Selecciona una persona" /></SelectTrigger>
                <SelectContent>
                  {people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Título *</Label>
              <Input value={form.title} onChange={(e) => setField("title", e.target.value)} placeholder="Ej: Mesociclo de hipertrofia" required data-testid="program-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo</Label>
                <Select value={form.type} onValueChange={(v) => setField("type", v)}>
                  <SelectTrigger data-testid="program-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="performance">Rendimiento (S&amp;C)</SelectItem>
                    <SelectItem value="clinical">Clínico</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Semanas</Label>
                <Input type="number" value={form.weeks} onChange={(e) => setField("weeks", e.target.value)} data-testid="program-weeks" />
              </div>
            </div>
            <div>
              <Label>Objetivo</Label>
              <Input value={form.goal} onChange={(e) => setField("goal", e.target.value)} placeholder="Ej: +5kg en sentadilla" data-testid="program-goal" />
            </div>
            <div>
              <Label>Fecha de inicio</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setField("start_date", e.target.value)} data-testid="program-start" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowNew(false)}>Cancelar</Button>
              <Button type="submit" className="rounded-full" disabled={saving} data-testid="program-save-btn">
                {saving ? "Guardando..." : "Crear programa"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
